import * as React from 'react';
import { AriaProperties, AriaRoles, generateDomPropertiesForAria } from 'owa-accessibility';
import {
    ContextualMenu,
    DirectionalHint,
    IContextualMenuItem,
} from 'office-ui-fabric-react/lib/components/ContextualMenu';
import { DefaultButton } from 'office-ui-fabric-react/lib/Button';
import { ColorPickerItem } from '../utils/Colors';
import { FocusZoneDirection } from 'office-ui-fabric-react/lib/FocusZone';
import Color from 'color';
import { DarkModeContext } from 'owa-theme';

const styles = require('./ColorPicker.scss');

type OnSelectColorCallback = (value: ColorPickerItem) => void;

export interface ColorPickerProps {
    menuTargetElement: HTMLElement;
    colors: ColorPickerItem[];
    onDismissMenu: () => void;
    onSelectColor: OnSelectColorCallback;
    shouldFocusOnMount?: boolean;
    selectedColor?: string;
}

type ColorMenuItemProps = {
    isSelected: boolean;
    onSelectColor: (color: ColorPickerItem) => void;
};

/**
 * true if the selected color and the color of this item are the same
 */
const areColorsEqual = (selectedColor, color) =>
    selectedColor !== null &&
    Color(color.code)
        .rgb()
        .string() ===
        Color(selectedColor)
            .rgb()
            .string();

/**
 * Individual color swatch in the color picker
 */
const ColorMenuItem = React.memo(
    (props: IContextualMenuItem & ColorMenuItemProps): JSX.Element => {
        const isDarkMode = React.useContext(DarkModeContext);
        const color = props.data as ColorPickerItem;
        // we always use light mode color for swatches.
        const inlineStyles: React.CSSProperties = {
            backgroundColor: color.colorCode.lightModeColor,
        };
        if (color.borderColor) {
            inlineStyles.borderColor = isDarkMode
                ? color.borderColor.darkModeColor
                : color.borderColor.lightModeColor;
        }

        const ariaProps: AriaProperties = {
            role: AriaRoles.menuitemcheckbox,
            label: color.name,
            checked: props.isSelected,
        };

        const onSelectColor = React.useCallback(() => {
            props.onSelectColor(color);
        }, [props.onSelectColor, color]);

        return (
            <DefaultButton
                data-is-focusable={true}
                title={color.name}
                onClick={onSelectColor}
                key={props.key}
                {...generateDomPropertiesForAria(ariaProps)}>
                <div className={styles.colorSquare} style={inlineStyles} />
            </DefaultButton>
        );
    }
);

/**
 * Context menu to select a color picker
 */
export const ColorPicker = (props: ColorPickerProps) => {
    const {
        selectedColor,
        onDismissMenu,
        onSelectColor,
        menuTargetElement,
        shouldFocusOnMount,
    } = props;

    const selectColorAndDismissContextMenu = React.useCallback(
        (color: ColorPickerItem) => {
            onDismissMenu();
            onSelectColor(color);
        },
        [onDismissMenu, onSelectColor]
    );

    const subItemRenderMethods = React.useMemo(
        () =>
            props.colors.map((color: ColorPickerItem) => (props: IContextualMenuItem) => (
                <ColorMenuItem
                    isSelected={areColorsEqual(selectedColor, color)}
                    onSelectColor={selectColorAndDismissContextMenu}
                    {...props}
                />
            )),
        [selectColorAndDismissContextMenu, props.colors]
    );

    const items = React.useMemo(
        () =>
            props.colors.map((color: ColorPickerItem, i) => {
                return {
                    key: color.name,
                    name: color.name,
                    onRender: subItemRenderMethods[i],
                    data: color,
                    className: styles.colorMenuItem,
                };
            }),
        [props.colors, selectColorAndDismissContextMenu]
    );

    return (
        <ContextualMenu
            className={styles.colorPickerContainer}
            shouldFocusOnMount={shouldFocusOnMount}
            target={menuTargetElement}
            directionalHint={DirectionalHint.bottomLeftEdge}
            onDismiss={onDismissMenu}
            focusZoneProps={{ direction: FocusZoneDirection.bidirectional }}
            items={items}
        />
    );
};
